/**
 * The browser half of the flow-testing kit (`docs/testing-flows.md`).
 *
 * Nothing here is a product surface, and nothing here renders unless `/api/health`
 * reports the identity port on its local stand-in — the same one question every
 * other dev affordance in this SPA hangs off (`xIdentityPortIsMock`).
 *
 * The reason a shared module exists rather than two copies of the same handler:
 * "start this identity over" is clicked from TWO places — the landing box,
 * signed out, and the floating strip, signed in as the account about to be
 * deleted — and the three steps have to happen in the same order both times or
 * the second run inherits the first one's clothes.
 */
import { useSyncExternalStore } from "react";
import { clearLocalSession } from "./draft";
import { resetDevUser, type DevResetResult } from "./api";

/**
 * WHICH ADDRESS IS WAITING FOR A CODE (ruling R-0826-3).
 *
 * The onboarding Email step and the Account page both post a six-digit code to
 * an address the tester just typed, and the strip is the thing that can go and
 * read it back — but only the screen knows the address, and only the strip
 * renders the answer. So the screen announces, in memory, and the strip
 * subscribes. Deliberately NOT storage: a reload is a fresh visit, and a code
 * remembered across one would be a code that has already expired.
 *
 * The announcement is unconditional; the READING is what is gated. The strip
 * itself renders only against the local identity stand-in and only for a
 * disposable `test_*` account, and the route it calls does not exist on a
 * deployed tier at all — so a product screen naming an address here costs
 * nothing anywhere the kit is not mounted.
 */
let pendingCodeEmail: string | null = null;
const pendingListeners = new Set<() => void>();

export function noteCodeSentTo(email: string | null): void {
  const next = email && email.trim() ? email.trim().toLowerCase() : null;
  if (pendingCodeEmail === next) return;
  pendingCodeEmail = next;
  for (const listener of pendingListeners) listener();
}

export function usePendingCodeEmail(): string | null {
  return useSyncExternalStore(
    (listener) => {
      pendingListeners.add(listener);
      return () => pendingListeners.delete(listener);
    },
    () => pendingCodeEmail,
    () => pendingCodeEmail,
  );
}

/**
 * Start this disposable identity over: the server forgets the account, the
 * browser forgets everything it stored about the person, and the page reloads
 * to a clean signed-out landing.
 *
 * THE ORDER IS THE POINT. The server first, because a local wipe that raced a
 * failed delete would leave a tester looking at a signed-out page in front of an
 * account that still exists. Then `clearLocalSession()` — the same sweep
 * sign-out uses, so a held knock, a pending pack and a half-typed draft all go
 * — and `sessionStorage` after it, which nothing in `lib/draft` owns but which a
 * screen may have parked a visit's state in.
 *
 * The reload is deliberately a NAVIGATION rather than a router push: the query
 * cache, the shell's module-level state and any effect mid-flight all belong to
 * the person who was signed in, and the cheapest honest way to be rid of all
 * three at once is a new document. Returns the server's refusal when it refused;
 * on success it does not return at all, because the page is already leaving.
 */
export async function resetAndStartOver(handle: string): Promise<DevResetResult> {
  const outcome = await resetDevUser(handle);
  if (!outcome.ok) return outcome;
  clearLocalSession();
  try {
    window.sessionStorage.clear();
  } catch {
    /* a browser with storage disabled has nothing to clear */
  }
  window.location.assign("/");
  return outcome;
}
